import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Session, User } from "@supabase/supabase-js";

export type AppRole = "super_admin" | "hotel_admin" | "hotel_staff" | "guest";

interface Profile {
  id: string;
  email: string;
  full_name: string;
  avatar_url: string | null;
  phone: string | null;
}

interface UserRole {
  role: AppRole;
  hotel_id: string | null;
  is_active: boolean;
  department: string | null;
}

interface AuthContextType {
  session: Session | null;
  user: User | null;
  profile: Profile | null;
  roles: UserRole[];
  /** True if the user has at least one active staff role (server-validated) */
  isActive: boolean;
  loading: boolean;
  signUp: (email: string, password: string, fullName: string) => Promise<{ error: Error | null }>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  hasRole: (role: AppRole) => boolean;
  isSuperAdmin: () => boolean;
  isHotelAdmin: (hotelId?: string) => boolean;
  isHotelStaff: (hotelId?: string) => boolean;
  getHotelId: () => string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [roles, setRoles] = useState<UserRole[]>([]);
  const [isActive, setIsActive] = useState(true);
  const [loading, setLoading] = useState(true);

  const fetchProfile = async (userId: string): Promise<Profile | null> => {
    const { data, error } = await supabase
      .from("profiles" as any)
      .select("*")
      .eq("id", userId)
      .maybeSingle();
    if (error) {
      console.error("[Auth] Failed to load profile", error);
      return null;
    }
    return (data as Profile | null) ?? null;
  };

  const fetchRoles = async (userId: string): Promise<UserRole[]> => {
    const { data, error } = await supabase
      .from("user_roles" as any)
      .select("role, hotel_id, is_active, department")
      .eq("user_id", userId);
    if (error) {
      console.error("[Auth] Failed to load roles", error);
      return [];
    }
    return (data as UserRole[] | null) ?? [];
  };

  /** Server-side check: does the user have at least one active staff role? */
  const checkIsActive = async (): Promise<boolean> => {
    const { data, error } = await supabase.rpc("is_staff_active" as any);
    if (error || data === null) {
      if (error) console.error("[Auth] Failed to check active staff status", error);
      return true;
    }
    return data as boolean;
  };

  const clearAuthState = () => {
    setSession(null);
    setUser(null);
    setProfile(null);
    setRoles([]);
    setIsActive(true);
  };

  const loadSession = async (nextSession: Session | null) => {
    if (!nextSession?.user) {
      clearAuthState();
      return;
    }

    setSession(nextSession);
    setUser(nextSession.user);

    const [nextProfile, nextRoles, nextIsActive] = await Promise.all([
      fetchProfile(nextSession.user.id),
      fetchRoles(nextSession.user.id),
      checkIsActive(),
    ]);

    setProfile(nextProfile);
    setRoles(nextRoles);
    setIsActive(nextIsActive);
  };

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (_event, session) => {
        if (_event === "INITIAL_SESSION") return;
        setLoading(true);
        await loadSession(session);
        setLoading(false);
      }
    );

    supabase.auth.getSession().then(async ({ data: { session } }) => {
      setLoading(true);
      await loadSession(session);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email: string, password: string, fullName: string) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { full_name: fullName },
        emailRedirectTo: window.location.origin,
      },
    });
    return { error: error as Error | null };
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error: error as Error | null };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    clearAuthState();
  };

  const hasRole = (role: AppRole) => roles.some((r) => r.role === role);
  const isSuperAdmin = () => hasRole("super_admin");
  const isHotelAdmin = (hotelId?: string) =>
    isSuperAdmin() || roles.some((r) => r.role === "hotel_admin" && (!hotelId || r.hotel_id === hotelId));
  const isHotelStaff = (hotelId?: string) =>
    isHotelAdmin(hotelId) || roles.some((r) => r.role === "hotel_staff" && (!hotelId || r.hotel_id === hotelId));
  const getHotelId = () => roles.find((r) => r.hotel_id)?.hotel_id ?? null;

  return (
    <AuthContext.Provider
      value={{ session, user, profile, roles, isActive, loading, signUp, signIn, signOut, hasRole, isSuperAdmin, isHotelAdmin, isHotelStaff, getHotelId }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
